import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
  useTheme
} from '@mui/material';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import { I_Folder, I_Installation } from 'src/interfaces/InstallationTypes';
import { InstallationsContext } from 'src/contexts/InstallationsContextProvider';
import { ProductIdContext } from 'src/contexts/ProductIdContextProvider';
import { FormattedMessage } from 'react-intl';

interface MoveModalProps {
  node: I_Installation | I_Folder;
  onClose: () => void;
}

// Returns the IDs of a folder and all its descendants (to prevent circular moves)
function getDescendantIds(folderId: number, allItems: any[]): Set<number> {
  const result = new Set<number>([folderId]);
  const queue = [folderId];
  while (queue.length > 0) {
    const currentId = queue.shift();
    allItems
      .filter((item) => item.parentId === currentId && item.type === 'Folder')
      .forEach((child) => {
        result.add(child.id);
        queue.push(child.id);
      });
  }
  return result;
}

function MoveModal(props: MoveModalProps) {
  const theme = useTheme();
  const { foldersAndInstallations, moveInstallation, moveFolder, loading, setLoading, error, setError } =
    useContext(InstallationsContext);
  const { product } = useContext(ProductIdContext);
  const [selectedFolderId, setSelectedFolderId] = useState<number | ''>('');

  // For folders: exclude self and all descendants to prevent circular nesting
  // For installations: any folder is valid
  const excludedIds =
    props.node.type === 'Folder'
      ? getDescendantIds(props.node.id, foldersAndInstallations)
      : new Set<number>();

  const availableFolders = foldersAndInstallations.filter(
    (item) => item.type === 'Folder' && !excludedIds.has(item.id)
  );

  const handleSubmit = async () => {
    if (selectedFolderId === '') return;
    setLoading(true);
    setError(false);
    if (props.node.type === 'Installation') {
      await moveInstallation(props.node.id, selectedFolderId as number, product);
    } else {
      await moveFolder(props.node.id, selectedFolderId as number, product);
    }
    setLoading(false);
    props.onClose();
  };

  return (
    <Modal
      open={true}
      onClose={() => {}}
      aria-labelledby="move-modal"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90vw', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 4,
          boxShadow: 24,
          p: 4
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
          <FormattedMessage id="moveTo" defaultMessage="Move to" />:{' '}
          <span style={{ color: theme.palette.primary.main }}>
            {props.node.name}
          </span>
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ fontSize: 14, backgroundColor: 'white' }}>
            <FormattedMessage id="folder" defaultMessage="Folder" />
          </InputLabel>
          <Select
            value={selectedFolderId}
            onChange={(e) => setSelectedFolderId(e.target.value as number)}
          >
            {availableFolders.map((folder: I_Folder) => (
              <MenuItem key={folder.id} value={folder.id}>
                {folder.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={selectedFolderId === ''}
            sx={{ marginLeft: '10px' }}
          >
            <FormattedMessage id="submit" defaultMessage="Submit" />
          </Button>

          <Button
            variant="contained"
            onClick={props.onClose}
            sx={{ marginLeft: '10px' }}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>

          {loading && (
            <CircularProgress
              sx={{ color: theme.palette.primary.main, marginLeft: '20px' }}
            />
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ ml: 1, display: 'flex', alignItems: 'center' }}
            >
              <FormattedMessage
                id="errorOccured"
                defaultMessage="An error has occurred"
              />
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setError(false)}
                sx={{ marginLeft: '4px' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Alert>
          )}
        </div>
      </Box>
    </Modal>
  );
}

export default MoveModal;
